import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import LegalPage, { InfoCard } from "@/components/LegalPage";
import { api } from "@/lib/api";
import {
  Mail, MessageSquare, Shield, Copyright, LifeBuoy, Info, ExternalLink, Send,
} from "lucide-react";

const TOPICS = [
  { key: "support", label: "General Support", icon: LifeBuoy },
  { key: "safety", label: "Trust & Safety", icon: Shield },
  { key: "billing", label: "Billing / Refunds", icon: Mail },
  { key: "dmca", label: "Copyright / DMCA", icon: Copyright },
  { key: "press", label: "Press / Partnerships", icon: MessageSquare },
  { key: "careers", label: "Careers", icon: MessageSquare },
];

function ContactForm() {
  const [topic, setTopic] = useState("support");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !subject || !body) { toast.error("Please fill in all fields."); return; }
    setBusy(true);
    try {
      await api.post("/contact", { topic, email, subject, body });
      setSent(true);
      toast.success("Message received — we'll reply within 5 business days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-lg border border-[color:var(--sc-volt)]/40 bg-[color:var(--sc-volt)]/5 p-6" data-testid="contact-sent">
        <div className="sc-heading text-xl font-bold text-white">Message received.</div>
        <p className="text-sm text-zinc-400 mt-2">
          Thanks — we&apos;ll get back to you at <strong>{email}</strong> within 5 business days. Trust & Safety reports are triaged faster.
        </p>
        <button
          onClick={() => { setSent(false); setSubject(""); setBody(""); }}
          className="sc-btn-ghost text-xs mt-4"
          data-testid="contact-send-another"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
      <div>
        <label className="sc-label block mb-2">What is this about?</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {TOPICS.map((t) => (
            <button
              type="button"
              key={t.key}
              onClick={() => setTopic(t.key)}
              className={`p-3 rounded text-left text-xs transition ${
                topic === t.key
                  ? "bg-[color:var(--sc-volt)]/15 border border-[color:var(--sc-volt)] text-white"
                  : "bg-black/30 border border-white/5 text-zinc-400 hover:border-white/20"
              }`}
              data-testid={`contact-topic-${t.key}`}
            >
              <t.icon className="w-3.5 h-3.5 mb-1.5" />
              <div>{t.label}</div>
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="sc-label block mb-2">Your email</label>
        <input
          type="email" className="sc-input" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com" required data-testid="contact-email"
        />
      </div>
      <div>
        <label className="sc-label block mb-2">Subject</label>
        <input
          className="sc-input" value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder="One-line summary" maxLength={140} required data-testid="contact-subject"
        />
      </div>
      <div>
        <label className="sc-label block mb-2">Message</label>
        <textarea
          className="sc-input resize-none" rows={6} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Tell us what's up. The more detail, the faster we can help."
          maxLength={4000} required data-testid="contact-body"
        />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10px] text-zinc-500 sc-mono">
          &gt; response_target: 5 business days · urgent safety: prioritized
        </div>
        <button type="submit" disabled={busy} className="sc-btn text-xs flex items-center gap-1.5" data-testid="contact-submit">
          <Send className="w-3.5 h-3.5" /> {busy ? "Sending..." : "Send Message"}
        </button>
      </div>
    </form>
  );
}

export default function Contact() {
  const sections = [
    {
      id: "email", title: "Direct Email", icon: Mail,
      body: (
        <p>
          Prefer email? <a href="mailto:support@mysticast.tv">support@mysticast.tv</a>. For urgent safety issues, please use the form below and select
          &quot;Trust & Safety&quot; so it&apos;s routed to the right people fast.
        </p>
      ),
    },
    {
      id: "form", title: "Send Us a Message", icon: Send, highlight: "volt",
      body: <ContactForm />,
    },
    {
      id: "directory", title: "Direct Email Addresses", icon: Mail, highlight: "cyan",
      body: (
        <>
          <p>Prefer to email? Route your message to the right mailbox for the fastest reply.</p>
          <ul className="space-y-1 mt-3">
            <li><strong>General support:</strong> <a href="mailto:support@mysticast.tv">support@mysticast.tv</a> — anything you're not sure where to send.</li>
            <li><strong>Billing, refunds, payouts:</strong> <a href="mailto:payments@mysticast.tv">payments@mysticast.tv</a> — include the transaction ID from your receipt email.</li>
            <li><strong>Privacy &amp; data requests:</strong> <a href="mailto:privacy@mysticast.tv">privacy@mysticast.tv</a> — GDPR/CCPA, export/delete, marketing opt-outs.</li>
            <li><strong>DMCA / copyright:</strong> <a href="mailto:dmca@mysticast.tv">dmca@mysticast.tv</a> — see the <Link to="/dmca">DMCA page</Link> for required notice contents.</li>
            <li><strong>Legal notices:</strong> <a href="mailto:legal@mysticast.tv">legal@mysticast.tv</a> — service of process, terms disputes, formal legal correspondence.</li>
            <li><strong>Press &amp; partnerships:</strong> <a href="mailto:owner@mysticast.tv">owner@mysticast.tv</a> — media inquiries, business development, brand deals.</li>
          </ul>
        </>
      ),
    },
    {
      id: "other", title: "Other Channels", icon: ExternalLink,
      body: (
        <ul>
          <li>For account bans/appeals, include your username and a link to the action being appealed.</li>
          <li>For payments issues, include the transaction ID from your receipt email.</li>
          <li>For DMCA notices, please follow the requirements on our <Link to="/dmca">DMCA page</Link>.</li>
        </ul>
      ),
    },
    {
      id: "hours", title: "Response Times", icon: Info,
      body: (
        <>
          <InfoCard icon={Info} title="⏱ Standard Support" tone="cyan">
            <p>General questions get a response within 5 business days.</p>
          </InfoCard>
          <InfoCard icon={Shield} title="🚨 Urgent Safety Reports" tone="pink">
            <p>Imminent harm, CSAM, doxxing — triaged first, usually within hours.</p>
          </InfoCard>
          <InfoCard icon={Copyright} title="© DMCA Notices" tone="orange">
            <p>Reviewed within 5 business days per our DMCA policy.</p>
          </InfoCard>
        </>
      ),
    },
  ];

  return (
    <LegalPage
      icon={Mail}
      label="CONTACT"
      title="Get in touch."
      intro="Whatever it is — question, bug, compliment, complaint, DMCA — we want to hear it."
      sections={sections}
      contact={{
        title: "Prefer to email directly?",
        body: "For most inquiries, use the form above. For anything else, we're at support@mysticast.tv.",
        cta: { label: "support@mysticast.tv", to: "/contact" },
      }}
    />
  );
}
